import { ArrowRight, Browser, ChartLineUp, Check, Flask, Play, PuzzlePiece, ShieldCheck } from "@phosphor-icons/react";

type GuideDestination = "extension" | "new";

const guideSteps = [
  {
    title: "安装一次浏览器插件",
    description: "下载 EchoLab Browser Relay，在 Chrome 开发者模式中加载。以后评测不需要重复安装。",
    icon: <PuzzlePiece size={20} />,
  },
  {
    title: "选择模板并开始评测",
    description: "确认网页链接、用户场景和模拟画像，然后点击一次“创建并开始评测”。",
    icon: <Flask size={20} />,
  },
  {
    title: "插件自动打开被测网页",
    description: "插件接收一次性授权，自动打开并只连接这次评测的目标网页，不需要填写配对信息。",
    icon: <Browser size={20} />,
  },
  {
    title: "用户模拟自动启动",
    description: "连接成功后 Agent 自动按用户场景观察、点击、输入和等待，并持续记录产品回复。",
    icon: <Play size={20} />,
  },
  {
    title: "随时观察或停止",
    description: "控制台同步显示执行进度。遇到异常时可以立即停止，插件会解除当前标签页控制。",
    icon: <ShieldCheck size={20} />,
  },
  {
    title: "查看评测结果",
    description: "独立评估器只根据过程证据归纳问题、评分和总结，结果可用于后续版本对比。",
    icon: <ChartLineUp size={20} />,
  },
];

const screenshotGuide = [
  {
    image: "/guides/03-extension-download.png",
    alt: "EchoLab 浏览器插件下载与安装页面",
    eyebrow: "截图 1 · 第一次只做一次",
    title: "先下载并安装浏览器插件",
    prompts: [
      ["点哪里", "点击页面右上方绿色的“下载插件 ZIP”。"],
      ["看到什么", "下载得到带有 v0.3.2 版本号的 ZIP，页面下方同时列出 Chrome 安装步骤。"],
      ["下一步", "解压 ZIP，在 chrome://extensions 打开开发者模式并加载解压后的文件夹。"],
    ],
  },
  {
    image: "/guides/01-config-template.png",
    alt: "EchoLab 配置库中的 Nexplay 测试环境模板",
    eyebrow: "截图 2 · 选择被测产品",
    title: "在配置库找到要测试的模板",
    prompts: [
      ["点哪里", "进入左侧“配置库”，在 nexplay-测试环境卡片右侧点击“使用模板”。"],
      ["看到什么", "模板会带入产品网址、用户场景和用户画像，不需要从空白开始填写。"],
      ["下一步", "系统自动进入“新建评测”，提交前仍然可以修改所有内容。"],
    ],
  },
  {
    image: "/guides/02-create-evaluation.png",
    alt: "EchoLab 使用 Nexplay 模板预填的新建评测页面",
    eyebrow: "截图 3 · 创建评测",
    title: "检查四项内容，然后一键开始",
    prompts: [
      ["检查什么", "模板名称、网页链接、用户场景和用户画像是否符合这次测试。"],
      ["点哪里", "看到“浏览器插件已就绪”后，点击右下角绿色的“创建并开始评测”。"],
      ["下一步", "插件会自动打开目标网页、完成连接并启动评测，不需要复制任何配对信息。"],
    ],
  },
];

export function GuideView({ onNavigate }: { onNavigate: (destination: GuideDestination) => void }) {
  return (
    <div className="guide-page">
      <div className="page-intro">
        <h2>第一次使用 EchoLab</h2>
        <p>EchoLab 让评测 Agent 像真实用户一样操作你指定的网页，并把操作、产品回复和最终结果整理成可复盘证据。</p>
      </div>

      <section className="panel guide-hero">
        <div>
          <span className="config-type">工作原理</span>
          <h3>控制台负责组织评测，插件负责操作当前网页。</h3>
          <p>你始终决定评测哪个页面、何时开始和何时停止。EchoLab 不会自动控制其他标签页，也不会把服务端密钥放进插件。</p>
        </div>
        <div className="guide-actions">
          <button className="button button-primary" onClick={() => onNavigate("extension")}><PuzzlePiece size={18} />先安装插件</button>
          <button className="button button-secondary" onClick={() => onNavigate("new")}>创建评测<ArrowRight size={17} /></button>
        </div>
      </section>

      <section className="system-map" aria-label="EchoLab 工作链路">
        <div><strong>EchoLab 控制台</strong><span>创建任务与查看进度</span></div>
        <ArrowRight size={18} />
        <div><strong>浏览器插件</strong><span>自动打开并连接目标网页</span></div>
        <ArrowRight size={18} />
        <div><strong>被测产品</strong><span>执行真实页面流程</span></div>
        <ArrowRight size={18} />
        <div><strong>评测报告</strong><span>归纳证据与问题</span></div>
      </section>

      <div className="guide-section-heading guide-visual-heading">
        <div>
          <span className="config-type">看图操作</span>
          <h3>跟着产品实际页面完成前三步</h3>
        </div>
        <p>截图来自当前 EchoLab 页面；界面升级后，按钮文字仍会保持一致。</p>
      </div>
      <section className="visual-guide" aria-label="EchoLab 产品截图操作指南">
        {screenshotGuide.map((item, index) => (
          <figure className="visual-guide-row" key={item.image}>
            <div className="visual-guide-shot">
              <img src={item.image} alt={item.alt} loading={index === 0 ? "eager" : "lazy"} />
            </div>
            <figcaption>
              <span className="config-type">{item.eyebrow}</span>
              <h3>{item.title}</h3>
              <ol>
                {item.prompts.map(([label, copy]) => (
                  <li key={label}>
                    <strong>{label}</strong>
                    <p>{copy}</p>
                  </li>
                ))}
              </ol>
            </figcaption>
          </figure>
        ))}
      </section>

      <aside className="pairing-prompt">
        <span className="pairing-prompt-number">!</span>
        <div>
          <strong>安装或更新插件后，刷新一次 EchoLab</strong>
          <p>Chrome 不会把新插件自动注入已经打开的旧页面。刷新后看到“浏览器插件已就绪”，以后每次评测都可以直接一键开始。</p>
        </div>
      </aside>

      <div className="guide-section-heading">
        <h3>完整操作流程</h3>
        <p>第一次使用通常先完成第 1 步，之后每次评测从第 2 步开始。</p>
      </div>
      <ol className="panel guide-steps">
        {guideSteps.map((step, index) => (
          <li key={step.title}>
            <span className="guide-step-number">{index + 1}</span>
            <span className="guide-step-icon">{step.icon}</span>
            <div><strong>{step.title}</strong><p>{step.description}</p></div>
            {index === 0 && <button className="text-action" onClick={() => onNavigate("extension")}>查看安装页<ArrowRight size={15} /></button>}
          </li>
        ))}
      </ol>

      <section className="guide-checklist">
        <div><Check size={18} weight="bold" /><span><strong>你需要准备</strong>桌面版 Chrome、浏览器插件，以及一个可以在浏览器打开的被测网页。</span></div>
        <div><Check size={18} weight="bold" /><span><strong>不用准备</strong>不需要把 Codex、Maxwell Key、System Prompt 或 Skills 安装进浏览器。</span></div>
      </section>
    </div>
  );
}
